import React, { useState, useEffect, useMemo } from 'react';
import { RefreshCw, AlertTriangle, FileText, Search, Plus } from 'lucide-react';
import { BlogPost, fetchAllPostsAdmin } from '../../services/blog';
import BlogEditDrawer from './BlogEditDrawer';

function statusOf(post: BlogPost): { label: string; cls: string } {
  if (!post.published_at) return { label: 'Mustand', cls: 'bg-gray-200 text-gray-600' };
  if (new Date(post.published_at) > new Date())
    return { label: 'Ajastatud', cls: 'bg-amber-100 text-amber-700' };
  return { label: 'Avaldatud', cls: 'bg-green-100 text-green-700' };
}

export default function BlogPanel() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<BlogPost | null>(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setPosts(await fetchAllPostsAdmin());
    } catch (err: any) {
      setError(err.message || 'Postituste laadimine ebaõnnestus');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!search) return posts;
    const q = search.toLowerCase();
    return posts.filter((p) =>
      [p.title, p.slug, p.excerpt, (p.tags || []).join(' ')].join(' ').toLowerCase().includes(q)
    );
  }, [posts, search]);

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Otsi pealkirja, slugi, sildi järgi..."
            className="w-full bg-white border border-gray-300 rounded-xl pl-9 pr-4 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4FC1E9] focus:border-transparent"
          />
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-xl text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 transition"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Uuenda
        </button>
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#4FC1E9] text-white rounded-xl text-sm font-medium hover:bg-[#3ba8d0] transition ml-auto"
        >
          <Plus className="w-4 h-4" />
          Uus postitus
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-64 text-gray-400">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          Laen postitusi...
        </div>
      )}

      {error && !loading && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((post) => {
            const st = statusOf(post);
            return (
              <div
                key={post.id}
                onClick={() => setEditing(post)}
                className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 overflow-hidden cursor-pointer hover:border-[#4FC1E9] hover:shadow-lg hover:shadow-[#4FC1E9]/10 transition-all duration-200 group flex flex-col"
              >
                <div className="relative h-36 bg-gray-100">
                  {post.image_url ? (
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FileText className="w-10 h-10 text-gray-300" />
                    </div>
                  )}
                  <span className={`absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>
                    {st.label}
                  </span>
                </div>
                <div className="p-3 flex-1 flex flex-col">
                  <p className="text-gray-900 font-semibold text-sm line-clamp-2">{post.title}</p>
                  <p className="text-xs text-gray-400 mt-1 truncate">/blogi/{post.slug}</p>
                  <p className="text-xs text-gray-500 mt-2 line-clamp-2 flex-1">{post.excerpt}</p>
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {(post.tags || []).slice(0, 3).map((t) => (
                      <span key={t} className="text-xs bg-[#4FC1E9]/10 text-[#4FC1E9] px-2 py-0.5 rounded-full">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center h-48 text-gray-500">
              <FileText className="w-12 h-12 mb-3 opacity-30" />
              <p>Postitusi ei leitud</p>
            </div>
          )}
        </div>
      )}

      {(editing || creating) && (
        <BlogEditDrawer
          post={editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            load();
          }}
          onDeleted={() => {
            setEditing(null);
            setCreating(false);
            load();
          }}
        />
      )}
    </div>
  );
}
